import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedPropertyV2 from 'customer-data-properties/formatted/FormattedPropertyV2';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID, DEAL_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { getObjectLabel } from 'customer-data-objects/objects/getObjectLabel';
import { useCallback } from 'react';
import getIn from 'transmute/getIn';
import styled from 'styled-components';
import UILink from 'UIComponents/link/UILink';
import { TABLE_BORDER_COLOR } from 'HubStyleTokens/theme';
import { OLAF } from 'HubStyleTokens/colors';
import { getRecordHref } from '../../utils/getRecordHref';
import { AvatarCell } from '../AvatarCell';
import EmptyState from '../EmptyState';
import TruncateDiv from '../TruncateDiv';
import DealSplitIcon from './DealSplitIcon';
import UITableHoverCell from 'UIComponents/table/UITableHoverCell';
import { shouldRightAlignProperty } from '../../utils/shouldRightAlignProperty';
import { useIsDealSplitsEnabled } from '../../table/hooks/useIsDealSplitsEnabled';
import { useAuth } from '../../auth/context/AuthContext';
import { useLogger } from '../../logger/context/LoggerContext';
const DEAL_SPLIT_COUNT_PROPERTY_NAME = 'hs_num_associated_deal_splits';

const getIcons = (objectTypeId, object, isDealSplitsEnabled) => {
  if (objectTypeId === DEAL_TYPE_ID && Number(getIn(['properties', DEAL_SPLIT_COUNT_PROPERTY_NAME, 'value'], object)) > 1 && isDealSplitsEnabled) {
    return /*#__PURE__*/_jsx(DealSplitIcon, {});
  }

  return null;
};

export const DefaultLinkComponent = ({
  href,
  role,
  onClick,
  children
}) => {
  return /*#__PURE__*/_jsx(UILink, {
    href: href,
    role: role,
    onClick: onClick,
    children: children
  });
};

const LabelCellContent = ({
  object,
  typeDef,
  propertyDefinition,
  onClick,
  getSecondaryPropertyValue,
  LinkComponent = DefaultLinkComponent
}) => {
  const {
    objectId
  } = object;
  const {
    objectTypeId
  } = typeDef;
  const logger = useLogger();
  const {
    hasGate
  } = useAuth();
  const recordHref = getRecordHref(objectTypeId, objectId, hasGate);
  const isDealSplitsEnabled = useIsDealSplitsEnabled(objectTypeId); // HACK: Technically this is a formatted name for contacts, but in all other
  // cases it is actually the value of primaryDisplayLabelPropertyName

  const value = getObjectLabel(typeDef, object);
  const onClickWithLogger = useCallback((...args) => {
    logger({
      action: 'label-cell-link-clicked',
      data: {
        objectTypeId,
        objectId
      }
    });

    if (onClick) {
      onClick(...args);
    }
  }, [logger, objectTypeId, onClick, objectId]);

  if ([CONTACT_TYPE_ID, COMPANY_TYPE_ID].includes(objectTypeId)) {
    return /*#__PURE__*/_jsx(AvatarCell, {
      type: objectTypeId === CONTACT_TYPE_ID ? 'vid' : 'companyId',
      id: objectId,
      href: recordHref,
      onClick: onClickWithLogger,
      displayName: value,
      children: value || /*#__PURE__*/_jsx(EmptyState, {})
    });
  }

  if (propertyDefinition) {
    const icons = getIcons(objectTypeId, object, isDealSplitsEnabled);
    return /*#__PURE__*/_jsx(LinkComponent, {
      role: "link",
      href: recordHref,
      onClick: onClickWithLogger,
      children: /*#__PURE__*/_jsxs(TruncateDiv, {
        rightAligned: shouldRightAlignProperty(propertyDefinition),
        children: [/*#__PURE__*/_jsx(FormattedPropertyV2, {
          objectTypeId: objectTypeId,
          objectId: objectId,
          property: propertyDefinition,
          value: value,
          getAdditionalPropertyValue: getSecondaryPropertyValue,
          getIsUngated: hasGate,
          dateFormat: "ll",
          dateTimeFormat: "ll"
        }), icons]
      })
    });
  }

  return /*#__PURE__*/_jsx(EmptyState, {});
};

const StickyLabelCell = styled(UITableHoverCell).withConfig({
  displayName: "LabelCell__StickyLabelCell",
  componentId: "b4ielb-0"
})(["background-color:", ";left:44px;outline:1px solid ", ";position:sticky;z-index:1;"], OLAF, TABLE_BORDER_COLOR);

const LabelCell = (_ref) => {
  let {
    hoverContent,
    requiredCellProps,
    rightSlot,
    isFixed
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["hoverContent", "requiredCellProps", "rightSlot", "isFixed"]);

  const Component = isFixed ? StickyLabelCell : UITableHoverCell;
  return /*#__PURE__*/_jsx(Component, Object.assign({
    hoverContent: hoverContent
  }, requiredCellProps, {
    children: /*#__PURE__*/_jsxs("div", {
      className: rightSlot ? 'flex-row align-center' : undefined,
      children: [/*#__PURE__*/_jsx(LabelCellContent, Object.assign({}, rest)), rightSlot]
    })
  }));
};

export default LabelCell;